import { useState, useRef, useEffect, Fragment, JSX } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleStop,
    faSort,
    faSortAlphaDown,
    faSortAlphaDownAlt,
    faSortAmountDown,
    faSortAmountDownAlt,
    faTicket,
} from '@fortawesome/free-solid-svg-icons';
import { formatMessage } from 'i18n/CurrentLocale';
import { useDebounce } from 'myParkdUtils/customHooks';
import { FormattedMessage } from 'react-intl';
import { isFreeSessionRight, isFixedDurationRight, isVariablePaidRight, calculateSessionDuration } from 'myParkdUtils/utils';
import moment from 'moment';
import ReactTable from 'react-table';
import ReactTableLoadingComponent from 'components/ReactTableLoadingComponent';
import ReactTableNoDataComponent from 'components/ReactTableNoDataComponent';
import 'moment/locale/nl';
import 'moment/locale/fr';
import { PagingReactTable, SessionDTOExtended, SessionRight, SessionUser, Status } from 'types';
import * as Sentry from '@sentry/react';
import { displayParkingSessionDates, displayTime, convertCentsToEuroValuta, stripStringFromNonAlphaNumericCharacters, formatMinutesDuration, calculateActiveSessionDuration } from 'myParkdUtils/utils';
import { Api } from 'api';
import { ModalEnum, PARKING_SESSIONS_TAB } from 'myParkdUtils/enums';
import { ColumnVisibility } from 'screens/modals/ManageTableColumnsModal';
import EditableTableField from 'components/EditableTableField';
import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import { isNotEmpty } from 'myParkdUtils/validationExpressions';
import { DEFAULT_COLUMNS_HISTORICAL_SESSIONS, DEFAULT_COLUMNS_OFFSTREET_SESSIONS } from './ParkingSessionsScreen';


type SessionRightClassName = 'paid' | 'fixed' | 'free' | 'invalid';

export const generateClassNameFromSessionRight = (right: SessionRight): SessionRightClassName => {
    if (isVariablePaidRight(right)) {
        return 'paid';
    } else if (isFixedDurationRight(right)) {
        return 'fixed';
    } else if (isFreeSessionRight(right)) {
        return 'free';
    } else {
        console.error('Unsupported sessionType found', right);
        Sentry.setContext('right', right);
        Sentry.captureMessage('Warning: Unsupported sessionType found (no CSS-class could be applied)');
        // we may have some sessions with durationType: null
        return 'invalid';
    }
};

export const getSessionUserName = (user: SessionUser | null): string => {
    if (user?.userGivenName && user?.userFamilyName) {
        return `${user?.userGivenName} ${user?.userFamilyName}`
    }
    return ''
}

export const SessionDurationCell = ({ row }: { row: SessionDTOExtended }): JSX.Element =>
    row.internalEndTime ? (
        <span className="time">
            {formatMinutesDuration(calculateSessionDuration(row))}&nbsp;
            <span className="time-start-end">{`(${moment(row.startTime).format('HH:mm')} - ${moment(row.endTime).format('HH:mm')})`}</span>
        </span>
    ) : (
        <span className="time">
            {calculateActiveSessionDuration(row.internalStartTime)}&nbsp;
            <span className="time-start-end">{`(${moment(row.startTime).format('HH:mm')} - ...)`}</span>
        </span>
    );

export const SessionCost = ({ row }: { row: SessionDTOExtended }): JSX.Element => (
    <div className="session-cost" title={isFixedDurationRight(row.right) ? formatMessage({ id: 'PARKING_HISTORY_DESCRIPTION_FIXED_DURATION_SESSION' }) : null}>
        {isFixedDurationRight(row.right) && (
            <FontAwesomeIcon icon={faTicket} size="xs" style={{ marginRight: '0.5em' }} data-testid='faTicket' />
        )}
        <span>{convertCentsToEuroValuta(row?.userCost!)}</span>
    </div>
);


const ParkingSessionView = (props: {
    userLocale: string;
    dateFrom?: string;
    dateTo?: string;
    updateSessions: Function;
    openedTab?: PARKING_SESSIONS_TAB;
    sessions: SessionDTOExtended[];
    paging: PagingReactTable;
    status: Status;
    openModalWindow?: Function;
    token?: string;
    defaultSorted: ColumnSort[];
    parkingSessionsColumnSettings?: ColumnVisibility | undefined;
}) => {
    const userLocale = props.userLocale;
    let reactSessionsTable: any = useRef(null);

    const [initialised, setInitialised] = useState(false);
    const [licensePlateToFilter, setLicensePlateToFilter] = useState('');
    const debouncedLicenseplate = useDebounce(licensePlateToFilter, 500);

    const { openedTab } = props;

    const defaultColumnVisibility = openedTab === PARKING_SESSIONS_TAB.TAB_HISTORICAL
        ? DEFAULT_COLUMNS_HISTORICAL_SESSIONS
        : DEFAULT_COLUMNS_OFFSTREET_SESSIONS

    const sessionsTableColumnSettings = { ...defaultColumnVisibility, ...props.parkingSessionsColumnSettings };

    useEffect(() => {
        moment.locale(userLocale);
    }, [userLocale]);

    useEffect(() => {
        setInitialised(true);
    }, []);

    useEffect(() => {
        if (initialised) {
            if (props.dateFrom && props.dateTo) {
                reactSessionsTable!.fireFetchData();
            }
        }
    }, [props.dateFrom, props.dateTo]);

    useEffect(() => {
        if (initialised) {
            reactSessionsTable.fireFetchData();
        }
    }, [debouncedLicenseplate]);

    const handleLicensePlateFilterChange = (licensePlate: string) => {
        const filteredLicensePlate = stripStringFromNonAlphaNumericCharacters(licensePlate).toUpperCase();
        setLicensePlateToFilter(filteredLicensePlate);
    };

    const onFetchData = (state: any) => {
        const filters: ColumnFilter[] = [
            { id: 'licensePlate', value: licensePlateToFilter },
            { id: 'dateFrom', value: props.dateFrom ?? '' },
            { id: 'dateTo', value: props.dateTo ?? '' }
        ].filter(cf => isNotEmpty(cf.value));

        const paging = {
            currentPage: state.page,
            pageSize: state.pageSize,
        };
        const sorting = state.sorted;
        props.updateSessions(filters, paging, sorting);
    };

    const stopSession = (session: SessionDTOExtended) => {
        props.openModalWindow!(ModalEnum.CONFIRMATION_DIALOG, {
            modalTitle: <FormattedMessage id='MODAL_STOP_SESSION_TITLE' />,
            modalText: <FormattedMessage id='MODAL_STOP_SESSION_TEXT' values={{ licensePlate: session?.localizedLicensePlate?.licensePlate }} />,
            confirmButtonText: <FormattedMessage id='MODAL_STOP_SESSION_BUTTON_CONFIRM' />,
            loadingMessage: <FormattedMessage id='MODAL_STOP_SESSION_LOADING_MESSAGE' />,
            successMessage: <FormattedMessage id='MODAL_STOP_SESSION_SUCCESS_MESSAGE' />,
            actionOnConfirm: async () => {
                await Api.stopSession(props.token!, session?.publicId!);
            },
            actionOnCompleted: () => {
                reactSessionsTable?.fireFetchData();
            },
            actionOnError: (error: any) => {
                console.error(error);
                reactSessionsTable?.fireFetchData();
            }
        });
    }

    const isHistoricalOrOffstreet =
        openedTab === PARKING_SESSIONS_TAB.TAB_HISTORICAL ||
        openedTab === PARKING_SESSIONS_TAB.TAB_OFFSTREET;

    const companySessionsTableColumns = [
        {
            Header: (
                <Fragment>
                    <FormattedMessage id="DATA_TABLE_LICENSEPLATE" />
                    <FontAwesomeIcon icon={faSort} className="header-icon-sorting" />
                    <FontAwesomeIcon icon={faSortAlphaDown} className="header-icon-asc" />
                    <FontAwesomeIcon icon={faSortAlphaDownAlt} className="header-icon-desc" />
                </Fragment>
            ),
            accessor: 'localizedLicensePlate.licensePlate',
            id: 'licensePlate',
            filterable: true,
            Filter: (
                <input
                    className="custom-filter-input"
                    data-testid="licenseplate-filter-input"
                    placeholder={formatMessage({ id: 'PLACEHOLDER_SEARCH' })}
                    value={licensePlateToFilter}
                    onChange={(e) => handleLicensePlateFilterChange(e.target.value)}
                />
            ),
            className: `rt-td-session`,
            style: { padding: 0 },
            Cell: (row: { original: SessionDTOExtended }) => (
                <div className={`session-lp-cell session-${generateClassNameFromSessionRight(row.original.right)}`}>
                    <span className='session-x' style={{}}>{row.original.localizedLicensePlate.licensePlate}</span>
                </div>
            ),
        },
        {
            Header: (
                <Fragment>
                    <FormattedMessage id="DATA_TABLE_COST" />
                    <FontAwesomeIcon icon={faSort} className="header-icon-sorting" />
                    <FontAwesomeIcon icon={faSortAmountDownAlt} className="header-icon-asc" />
                    <FontAwesomeIcon icon={faSortAmountDown} className="header-icon-desc" />
                </Fragment>
            ),
            accessor: 'userCost',
            id: 'userCost',
            show: isHistoricalOrOffstreet ? true : false,
            filterable: false,
            width: 170,
            Cell: (row: { original: SessionDTOExtended }) => <SessionCost row={row.original} />
        },
        {
            Header: (
                <Fragment>
                    <FormattedMessage id="DATA_TABLE_ZONECODE" />
                    <FontAwesomeIcon icon={faSort} className="header-icon-sorting" />
                    <FontAwesomeIcon icon={faSortAmountDownAlt} className="header-icon-asc" />
                    <FontAwesomeIcon icon={faSortAmountDown} className="header-icon-desc" />
                </Fragment>
            ),
            accessor: 'location.displayCode',
            id: 'posCode',
            show: openedTab !== PARKING_SESSIONS_TAB.TAB_OFFSTREET,
            filterable: false,
            Cell: ({ value }: { value: string }) => <span>{value}</span>,
        },
        {
            Header: <FormattedMessage id="DATA_TABLE_LOCATION" />,
            accessor: 'location',
            id: 'location',
            filterable: false,
            Cell: ({ value }: { value: { address: string } }) => <span>{value?.address}</span>,
        },
        {
            Header: (
                <Fragment>
                    <FormattedMessage id="DATA_TABLE_DATE" />
                    <FontAwesomeIcon icon={faSort} className="header-icon-sorting" />
                    <FontAwesomeIcon icon={faSortAlphaDown} className="header-icon-asc" />
                    <FontAwesomeIcon icon={faSortAlphaDownAlt} className="header-icon-desc" />
                </Fragment>
            ),
            accessor: 'date',
            id: 'startTime',
            filterable: false,
            Cell: (row: { original: SessionDTOExtended }) => {
                return displayParkingSessionDates(row.original.startTime, row.original.endTime);
            },
        },
        {
            Header: (
                <Fragment>
                    <FormattedMessage id="DATA_TABLE_DURATION" />
                    <FontAwesomeIcon icon={faSort} className="header-icon-sorting" />
                    <FontAwesomeIcon icon={faSortAmountDownAlt} className="header-icon-asc" />
                    <FontAwesomeIcon icon={faSortAmountDown} className="header-icon-desc" />
                </Fragment>
            ),
            accessor: 'duration',
            id: 'duration',
            show: openedTab !== PARKING_SESSIONS_TAB.TAB_OFFSTREET,
            filterable: false,
            Cell: (row: { original: SessionDTOExtended }) => {
                return <SessionDurationCell row={row.original} />;
            },
        },
        {
            Header: (
                <FormattedMessage id="DATA_TABLE_TRANSACTION_TIME" />
            ),
            accessor: 'internalEndTime',
            id: 'endTime',
            show: openedTab === PARKING_SESSIONS_TAB.TAB_OFFSTREET,
            filterable: false,
            Cell: (row: { original: SessionDTOExtended }) => {
                return displayTime(row.original.internalEndTime);
            }
        },
        {
            Header: <FormattedMessage id="DATA_TABLE_NOTE" />,
            accessor: 'sessionAnnotation.sessionNote',
            id: 'note',
            sortable: false,
            show: openedTab === PARKING_SESSIONS_TAB.TAB_HISTORICAL && sessionsTableColumnSettings['sessionAnnotation.sessionNote'] ? true : false,
            filterable: false,
            Cell: (row: { original: SessionDTOExtended }) => (
                <EditableTableField
                    defaultValue={row.original?.sessionAnnotation?.sessionNote!}
                    placeholder={formatMessage({ id: 'PLACEHOLDER_ADD_NOTE' })}
                    modalName={ModalEnum.EDIT_SESSION_NOTE}
                    rowData={row.original}
                />
            )
        },
        {
            Header: <FormattedMessage id="DATA_TABLE_COST_CENTER" />,
            accessor: 'sessionAnnotation.costCenter',
            id: 'costCenter',
            sortable: false,
            show: sessionsTableColumnSettings['sessionAnnotation.costCenter'] ? true : false,
            filterable: false,
            Cell: ({ value }: { value: string }) => <span>{value}</span>
        },
        {
            Header: <FormattedMessage id="DATA_TABLE_DRIVER_NAME" />,
            accessor: 'user',
            id: 'user',
            sortable: false,
            show: openedTab === PARKING_SESSIONS_TAB.TAB_OFFSTREET && sessionsTableColumnSettings['user'] ? true : false,
            filterable: false,
            Cell: (row: { original: SessionDTOExtended }) => {
                return getSessionUserName(row.original.user);
            }
        },
        {
            Header: <FormattedMessage id="DATA_TABLE_DRIVER_EMAIL" />,
            accessor: 'email',
            id: 'email',
            sortable: false,
            show: isHistoricalOrOffstreet && !!sessionsTableColumnSettings.email,
            filterable: false,
            Cell: ({ value }: { value: string }) => <span>{value}</span>
        },
        {
            Header: (<FormattedMessage id="DATA_TABLE_ACTIONS" />),
            show: openedTab === PARKING_SESSIONS_TAB.TAB_ACTIVE ? true : false,
            filterable: false,
            sortable: false,
            Cell: (row: { original: SessionDTOExtended }) => {
                return (
                    <button className='btn-small-red' onClick={() => stopSession(row.original)}>
                        <FontAwesomeIcon icon={faCircleStop} />&nbsp;
                        <FormattedMessage id="BTN_STOP_ACTIVE_SESSION" />
                    </button>
                )
            }
        }
    ];


    const hasError = !!props.status.error;

    return (
        <div translate='no' /* prevents table crash when using google's live translation */>
            <ReactTable
                className='parkd-react-table'
                getTrProps={(state: any, rowInfo: any) => {
                    const sessionClassName = generateClassNameFromSessionRight(rowInfo.row._original.right);
                    return { className: `session-${sessionClassName}-row ` };
                }}
                data={props.sessions}
                filterable={true}
                resizable={true}
                resolveData={(data: any) => data.map((row: { original: SessionDTOExtended }) => row)}
                columns={companySessionsTableColumns}
                minRows={0}
                showPagination={props.paging.totalPages! > 1}
                manual={true} // server side pagination
                onFetchData={onFetchData}
                pages={props.paging.totalPages}
                pageSizeOptions={[50, 100, 200]}
                defaultPageSize={100}
                defaultSorted={props.defaultSorted}
                loading={props.status.loading}
                LoadingComponent={ReactTableLoadingComponent}
                noDataText={props.status.loading ? '' : formatMessage({ id: 'REACT_TABLE_NO_DATA_SESSIONS' })}
                NoDataComponent={(props) => (<ReactTableNoDataComponent {...props} error={hasError} />)}
                previousText={formatMessage({ id: 'REACT_TABLE_PREVIOUS' })}
                nextText={formatMessage({ id: 'REACT_TABLE_NEXT' })}
                loadingText={formatMessage({ id: 'REACT_TABLE_LOADING' })}
                pageText={formatMessage({ id: 'REACT_TABLE_PAGE' })}
                ofText={formatMessage({ id: 'REACT_TABLE_OF' })}
                rowsText={formatMessage({ id: 'REACT_TABLE_SESSION_ROWS' })}
                ref={(instance: any) => {
                    reactSessionsTable = instance;
                }}
            />
        </div>
    );
};

export default ParkingSessionView;